import { AxiosError } from "axios"
import clientApi from "@/shared/api/client"
import { ENTRY_FORM_URL, SIGN_IN_URL, SIGN_UP_URL, USER } from "@/entities/user/api/url"
import { ApiResponse } from "@/shared/types/interface/api"
import {
  GetInterestResponse,
  GetUserInfoResponse,
  GetUserInquiryHistoryResponse,
  GetUserReviewHistoryResponse,
  PostEntryFormRequest,
  PostInquiryRequest,
  PostInterestRequest,
  PostUnrestrictedInquiryRequest,
  SignInResponse,
} from "@/shared/types/type/user"
import { HOSPITAL_URL } from "@/entities/hospital/api/url"
import { ERROR_CODE } from "@/shared/constants/errorCode"
import { SignInRequest } from "@/shared/types/type/sign-in"
import { SignUpRequest } from "@/shared/types/type/sign-up"

export const getUserInfo = async () => {
  try {
    const response = await clientApi.get(USER.INFO)
    if (response.status !== 200) {
      throw new Error("Failed to get user info")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to get user info")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<GetUserInfoResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postUserInterest = async (interestList: PostInterestRequest) => {
  try {
    const response = await clientApi.post(USER.INTEREST, interestList)
    if (response.status !== 200) {
      throw new Error("Failed to post user interest")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to post user interest")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<unknown>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getUserInterest = async () => {
  try {
    const response = await clientApi.get(USER.INTEREST)
    if (response.status !== 200) {
      throw new Error("Failed to get user interest")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to get user interest")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<GetInterestResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postUserInquiry = async (inquiry: PostInquiryRequest) => {
  try {
    const response = await clientApi.post(USER.INQUIRY, inquiry)
    if (response.status !== 200) {
      throw new Error("Failed to post user inquiry")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to post user inquiry")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<unknown>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getUserInquiryHistoryClient = async (page: number) => {
  try {
    const response = await clientApi.get(USER.INQUIRY_HISTORY(page))
    if (response.status !== 200) {
      throw new Error("Failed to get user inquiry history")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to get user inquiry history")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<GetUserInquiryHistoryResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postHospitalFavoriteClient = async (hospitalOriginId: number) => {
  try {
    const response = await clientApi.post(HOSPITAL_URL.FAVORITE, {
      hospitalOriginId,
    })
    if (response.status !== 200) {
      throw new Error("Failed to post hospital favorite")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to post hospital favorite")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<unknown>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const deleteHospitalFavoriteClient = async (hospitalOriginId: number) => {
  try {
    const response = await clientApi.delete(HOSPITAL_URL.FAVORITE, {
      data: {
        hospitalOriginId,
      },
    })
    if (response.status !== 200) {
      throw new Error("Failed to delete hospital favorite")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to delete hospital favorite")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<unknown>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getUserReviewHistoryClient = async (page: number) => {
  try {
    const response = await clientApi.get(USER.REVIEW_HISTORY(page))
    if (response.status !== 200) {
      throw new Error("Failed to get user review history")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to get user review history")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<GetUserReviewHistoryResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postEntryFormClient = async (entryForm: PostEntryFormRequest) => {
  try {
    const response = await clientApi.post(ENTRY_FORM_URL.SUBMIT, entryForm)
    if (response.status !== 200) {
      throw new Error("Failed to post entry form")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to post entry form")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<unknown>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postUnrestrictedInquiry = async (req: PostUnrestrictedInquiryRequest) => {
  try {
    const response = await clientApi.post(USER.UNRESTRICTED_INQUIRY, req)
    if (response.status !== 200) {
      throw new Error("Failed to post unrestricted inquiry")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to post unrestricted inquiry")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<unknown>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const signInClient = async ({ email, password }: SignInRequest) => {
  try {
    const response = await clientApi.post(SIGN_IN_URL.EMAIL, {
      email,
      password,
    })

    if (response.status !== 200) {
      throw new Error("No Response")
    }

    if (response.data.status !== 200) {
      const error = new AxiosError(ERROR_CODE[response.data.code as keyof typeof ERROR_CODE] || "Unknown Error")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }

    return response.data as ApiResponse<SignInResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const signUp = async ({ name, email, password, rePassword }: SignUpRequest) => {
  try {
    const response = await clientApi.post(SIGN_UP_URL, {
      name,
      email,
      password,
      rePassword,
    })

    if (response.status !== 200) {
      throw new Error("No Response")
    }

    if (response.data.status !== 200) {
      const error = new AxiosError(ERROR_CODE[response.data.code as keyof typeof ERROR_CODE] || "Unknown Error")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }

    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}
